<template>
  <div class="flex gap-x-2">
    <div class="relative" @click="emit('avatar-click')">
      <picture :data-testid="dataTestid+'-picture'">
        <TsImage
            v-if="props.image"
            :data-testid="dataTestid+'-img'"
            :class="[AVATAR_SIZES[props.size], props.roundness, props.bordered ? 'border-2 border-white rounded-full dark:border-gray-800' : '']"
            :src="props.image"
            class="object-cover"
            loading="lazy"
        />
        <div
            v-else
            v-bind="$attrs"
            :data-testid="dataTestid+'-div'"
            :class="
            $attrs.class || twMerge(
              AVATAR_SIZES[props.size],
              props.name?.length
              ? 'bg-primary'
              : 'bg-natural-light-grey dark:bg-natural-dark-grey',
              'flex justify-center items-center font-extrabold text-white',
              props.roundness,
              props.bordered ? 'border-2 border-white rounded-full dark:border-gray-800' : '',
              props.appendClass)"
        >
          <span
              class="tracking-[-2px]"
              v-if="props.name?.length"
              :data-testid="props.dataTestid+'-span'"
              :class="{ sm: 'text-xs', md: 'text-xs', lg: 'text-lg' }[props.size]"
          >{{ getNameInitials() }}</span
          >
          <slot v-else name="placeholder">
          <TsIcon
              name="heroicons:user-circle"
              :size="{ sm: '14', md: '18', lg: '24' }[props.size]"
              class="text-natural-grey"
              :data-testid="props.dataTestid+'-icon'"

          />
          </slot>
        </div>
      </picture>
      <span
          v-if="props.status !== 'none'"
          :data-testid="props.dataTestid+'-span'"
          :class="[
          INDICATOR_POSITIONING[props.size],
          INDICATOR_COLOR[props.status],
        ]"
          class="bottom-0  text-white flex items-center justify-center absolute border-idle-white dark:border-black rounded-full"
      >
        <slot name="status-content">
        <span v-if="status === 'notification'" :data-testid="props.dataTestid+'-span'" class="text-xs"> {{ notificationCount }} </span>
          <template v-else>
                    <TsIcon
                        v-if="ICONS[status]"
                        :name="ICONS[status]"
                        :size="{ sm: '6', md: '8', lg: '12' }[props.size]"
                        :data-testid="props.dataTestid+'-icon'"
                    />
          </template>
        </slot>
      </span>
    </div>
    <div class="flex flex-col items-start">
      <slot name="content">
        <TsTypography v-if="props.name" appendClass="text-natural-dark-grey" weight="bold" >{{ props.name }}</TsTypography>
        <TsTypography v-if="props.description && props.name">{{ props.description }}</TsTypography>
      </slot>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { twMerge } from "tailwind-merge";
const emit = defineEmits(["avatar-click"]);
type Props = {
  size?: "sm" | "md" | "lg";
  status?:
      | "none"
      | "active"
      | "offline"
      | "away"
      | "notification"
      | "remove"
      | "secure"
      | "edit"
      | "cta";
  image?: string;
  name?: string;
  description?: string;
  roundness?: string;
  appendClass?: string;
  bordered?: boolean;
  dataTestid?:string;
  notificationCount?: number;
};

const props = withDefaults(defineProps<Props>(), {
  size: "md",
  status: "none",
  roundness: 'rounded-full',
  bordered: false,
  dataTestid:'atom-avatar',
  notificationCount: 2
});

const ICONS: Record<string, any> = {
  remove: "ant-design:close-outlined",
  edit: "lucide:pencil",
  secure: "mdi:lock-outline",
  cta: "charm:plus",
};

const getNameInitials = () => {
  const text = props.name?.split(" ");
  const getChar = (n: number) => text?.at(n)?.slice(0, 1)?.toUpperCase();
  return `${getChar(0)} ${text!.length > 1 ? getChar(-1) : ""}`;
};

const AVATAR_SIZES = {
  sm: "w-6 h-6",
  md: "w-8 h-8",
  lg: "w-12 h-12",
};

const INDICATOR_POSITIONING = {
  sm: "left-4 w-2 h-2 border text-xs",
  md: "left-5 w-3 h-3 border-2 text-xs",
  lg: "left-8 w-4 h-4 border-2",
};

const INDICATOR_COLOR = {
  none: "",
  active: "bg-success dark:bg-success-dark",
  offline: "bg-danger dark:bg-danger-dark",
  away: "bg-natural-silver-grey",
  notification:
      "bg-info dark:bg-primaryGradientDodgerBlueDark border-none top-0",
  remove: "bg-danger dark:bg-danger-dark border-none",
  secure: "bg-black dark:bg-natural-grey border-none",
  edit: "bg-success dark:bg-success-dark border-none",
  cta: "bg-info dark:bg-primaryGradientDodgerBlueDark border-none",
};
</script>
